
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'

// types
import { NotificationItTemplateT } from '@/core/types/settings.types'
import { CompanyT } from '@/core/types/companies.types'

// realisations
import { settings } from '@/core/realisations/settings.realisation'
import { companies } from '@/core/realisations/companies.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

export default defineComponent({
    components: { Modal, TheSelect },
    props: {
        id: {
            type: Number,
            default: null,
        },
    },
    emits: ['updated', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            companyIds: [] as number[],
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
        })

        const state_form$ = useVuelidate(
            {
                companyIds: {
                    $each: { required },
                },
            },
            state
        )

        const closeModal = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const updateCompanies = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                await settings.editCompaniesNotificationIt(props.id, state.companyIds)

                notifications.notify({
                    title: 'Компании успешно отредактированы!',
                    type: 'success',
                })
                emit('updated')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании компаний произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadNotificationIt = async () => {
            if (!props.id) return

            try {
                state.is_loading = true

                const response = await settings.getNotificationIt(props.id)
                if (response.data) {
                    state.companyIds = response.data.companies.map((company) => company.id)
                }
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        //

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = { page, statusId: 2, isExtended: 0, search }

                const response = await companies.getList(params)
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = { page, statusId: 2, isExtended: 0 }

                const response = await companies.getList(params)
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
            } finally {
                data_state.companies.is_loading = false
            }
        }

        const searchCompanies = (search_query: string) => {
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        //

        onBeforeMount(() => {
            loadCompanies(1)
            loadNotificationIt()
        })

        return {
            state,
            data_state,
            //
            state_form$,
            //
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            //
            updateCompanies,
            closeModal,
        }
    },
})
